
import { useIntl } from 'react-intl';
import PageContentHeader from '../../shared/components/page-content-header/PageContentHeader';
import './Dashboard.less';
import { Row, Col, Card, Select, Space, Table } from 'antd';
import ItemList from '../../shared/components/item-list/ItemListView';
import { useEffect, useState } from 'react';
import DashboardFilter from './DashboardFilter';
import { formatToTimeZone, getCurrentDateInTimeZone, getEndOfDayInTimeZone, getStartOfDayInTimeZone } from '../../shared/helpers/date-format.helper';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useQuery } from '@apollo/client';
import { GET_DASHBOARD } from './Dashboard.gql';
import { formatNumber } from '../../shared/helpers/number.helper';
import moment from 'moment';


const Dashboard = () => {
  const intl = useIntl();
  const pageTitle = intl.formatMessage({ id: "ACCOUNT_STATISTIC" });
  let _elements: any = <></>;
  const [filters, setFilters] = useState({
    currencyId: "",
    bankId: "",
    startDate: formatToTimeZone(getStartOfDayInTimeZone(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
    endDate: formatToTimeZone(getEndOfDayInTimeZone(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
  });
  const { data, loading, refetch } = useQuery(GET_DASHBOARD, {
    variables: { filterInput: { ...filters } },
    fetchPolicy: 'cache-and-network'
  });
  const [dashboard, setDashboard] = useState<any>();

  useEffect(() => {
    if (data) {
      let _data: any = [];
      if (data.dashboard && data.dashboard.length > 0) {
        _data = data.dashboard.map((val: any) => {
          return {
            ...val,
            key: val.id
          };
        });
      }
      setDashboard(_data);
    }
  }, [data]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const processedData = () => {
    const grouped: any[] = [];

    const byCurrency: Record<string, any[]> = dashboard?.reduce((acc: any, row: any) => {
      acc[row.currencyId] = acc[row.currencyId] || [];
      acc[row.currencyId].push(row);
      return acc;
    }, {});

    for (const currency in byCurrency) {
      // Calculate total net amount for this currency
      let depositAmount = 0;
      let payoutAmount = 0;
      let settlementAmount = 0;

      byCurrency[currency].forEach(row => {
        if (row.operation === 'DEPOSIT') {
          depositAmount += +(row.netAmount || 0);
        } else if (row.operation === 'PAYOUT') {
          payoutAmount += +(row.netAmount || 0);
        } else if (row.operation === 'SETTLEMENT') {
          settlementAmount += +(row.netAmount || 0);
        }
      });

      const totalNetAmount = +(depositAmount - payoutAmount - settlementAmount).toFixed(2);

      grouped.push({
        key: `currency-${currency}`,
        isCurrencyHeader: true,
        currencyId: currency,
        currencyName: currency,
        netAmount: totalNetAmount // Add the calculated total to the header row
      });

      grouped.push(...byCurrency[currency]);
    }

    return grouped;
  };

  const columns: any = [
    {
      title: intl.formatMessage({ id: 'OPERATION' }),
      dataIndex: 'operation',
      render: (item: any, record: any) => {
        if (record.isCurrencyHeader) {
          return <strong>{`${record.currencyId}`}</strong>;
        }
        return <Space size="middle">{intl.formatMessage({ id: item })}</Space>;
      },
    },
    {
      title: intl.formatMessage({ id: 'SUCCESS_RATE' }),
      dataIndex: 'successRate',
      render: (item: any, record: any) => {
        if (record.isCurrencyHeader || record.operation === "SETTLEMENT") return null;
        const rate = +record?.totalCount > 0 ? ((+record?.successCount / +record?.totalCount) * 100).toFixed(2) : "0.00";
        return <Space size="middle">{rate + "%"}</Space>;
      },
    },
    {
      title: intl.formatMessage({ id: 'SUCCESS_COUNT' }) + "/" + intl.formatMessage({ id: 'TOTAL_COUNT' }),
      dataIndex: 'successCount',
      render: (item: any, record: any) => {
        if (record.isCurrencyHeader || record.operation === "SETTLEMENT") return null;
        return <Space size="middle">{item + "/" + record?.totalCount}</Space>;
      },
    },
    {
      title: intl.formatMessage({ id: 'SUCCESS_AMOUNT' }),
      dataIndex: 'successAmount',
      render: (item: any, record: any) => {
        if (record.isCurrencyHeader) return null;
        return <Space size="middle">{formatNumber(intl, item, 2)}</Space>;
      },
    },
    {
      title: intl.formatMessage({ id: 'SUCCESS_FEE' }),
      dataIndex: 'successFee',
      render: (item: any, record: any) => {
        if (record.isCurrencyHeader) return null;
        return <Space size="middle">{formatNumber(intl, item, 2)}</Space>;
      },
    },
    {
      title: intl.formatMessage({ id: 'NET_AMOUNT' }),
      dataIndex: 'netAmount',
      render: (item: any, record: any) => {
        if (record.isCurrencyHeader) {
          // Show total net amount for currency header row
          return <Space size="middle"><strong>{formatNumber(intl, item, 2)}</strong></Space>;
        }
        return <Space size="middle">{formatNumber(intl, item, 2)}</Space>;
      },
    },
  ];

  return (
    <>
      <PageContentHeader content={{
        title: `${pageTitle} `,
        additionalElement: _elements
      }} />
      <DashboardFilter setFilters={setFilters} filters={filters} />
      <Table
        size="small"
        columns={columns}
        dataSource={processedData()}
        pagination={false}
        loading={loading}
        bordered
        rowClassName={(record) => (record.isCurrencyHeader ? 'currency-header-row' : '')}
      />
    </>
  );
}

export default Dashboard;